import { useContext, useSyncExternalStore } from 'react';
import SilverstripeContext from '@/components/context/Silverstripe';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '@discover-data/stores/restaurant/model/times/default';
import { DEFAULT_DATE } from '@discover-data/stores/restaurant/model/date/defaults';
import DEFAULT_FAVOURITE from '@discover-data/stores/restaurant/model/favourite/default';
import DEFAULT_HAS_LAST_TABLE from '@discover-data/stores/restaurant/model/hasLastTable/default';
import DEFAULT_PEOPLE from '@discover-data/stores/restaurant/model/people/default';
import DEFAULT_SESSION from '@discover-data/stores/restaurant/model/session/default';
import DEFAULT_SORT from '@discover-data/stores/restaurant/model/sort/default';
import DEFAULT_TAG_IDS from '@discover-data/stores/restaurant/model/tagIds/default';
import { sessionSlugs } from '@discover-lib/getCitySlug';
import useCitySlug from './useCitySlug';
import useDateQueryState from './date/useDateQueryState';
import useDiscountOnlyQueryState from './discountOnly/useQueryState';
import useRegularOnlyQueryState from './regularOnly/useQueryState';
import useHasLastTableQueryState from './hasLastTable/useQueryState';
import usePeopleQueryState from './people/useQueryState';
import useSortQueryState from './sort/useQueryState';
import useTagIdsQueryState from './tagIds/useQueryState';
import useStartTimeQueryState from './times/start/useStartTimeQueryState';
import useEndTimeQueryState from './times/end/useEndTimeQueryState';
import type { UseSyncInputsProps } from './useSyncInputs';

const subscribe = () => () => undefined;
const clientSnapshot = () => true;
const serverSnapshot = () => false;

/**
 * Returns true when any restaurant list filter is active.
 *
 * Mirrors the filter-count logic used by `RestaurantFiltersDrawer` and also treats non-default list-changing state outside the drawer as active filters.
 */
export default function useHasActiveFilters({
  tags,
  peopleOptions,
  minDiners,
  maxDiners,
}: UseSyncInputsProps): boolean {
  // The store receives these values in effects; URL values are available on the initial render.
  const [tagIds] = useTagIdsQueryState(tags);
  // Selection updates the store before its shallow URL navigation completes.
  const hasSelectedTags = useStoreState((state) =>
    state.tagIds.value.some((id) => !DEFAULT_TAG_IDS.includes(id))
  );
  const hasSelectedSuburbs = useStoreState((state) => state.suburb.ids.length > 0);
  const [{ suburbs, session }] = useCitySlug();
  const [hasLastTable] = useHasLastTableQueryState();
  const [startTime] = useStartTimeQueryState();
  const [endTime] = useEndTimeQueryState();
  const [regularOnly] = useRegularOnlyQueryState();
  const [discountOnly] = useDiscountOnlyQueryState();
  const [sort] = useSortQueryState();
  const [date] = useDateQueryState();
  const [people] = usePeopleQueryState(peopleOptions, minDiners, maxDiners);
  const favourite = useStoreState((state) => state.favourite.value);

  const { page } = useContext(SilverstripeContext);
  const hydrated = useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);
  if (!hydrated) {
    // Static HTML has no query string, so until hydration report the filter state the page was built with: a non-default meal session, a cuisine page or a suburb location. The query-state hooks and the store apply after.
    const isSessionPage =
      page?.pageAction === 'session' && page.pageActionValue !== sessionSlugs[DEFAULT_SESSION];
    const isSuburbPage =
      page?.filters?.suburbId != null && String(page.filters.suburbId) !== String(page.regionId);
    return isSessionPage || page?.pageAction === 'cuisine' || isSuburbPage;
  }

  const selectedTagIds = tagIds.filter((id) => !DEFAULT_TAG_IDS.includes(id));

  return (
    hasSelectedTags ||
    hasSelectedSuburbs ||
    selectedTagIds.length > 0 ||
    suburbs.length > 0 ||
    hasLastTable !== DEFAULT_HAS_LAST_TABLE ||
    startTime !== DEFAULT_START_TIME ||
    endTime !== DEFAULT_END_TIME ||
    regularOnly ||
    discountOnly ||
    sort !== DEFAULT_SORT ||
    date !== DEFAULT_DATE ||
    people !== DEFAULT_PEOPLE ||
    session !== DEFAULT_SESSION ||
    favourite !== DEFAULT_FAVOURITE
  );
}
